import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Info, ShieldAlert, X } from "lucide-react";

/**
 * Persistent disclosure footer. Two layers:
 *   - Always-visible thin strip (short line + Terms/Privacy links)
 *   - Click "Details" to expand a full compliance panel with the
 *     Educational Tool Notice + Risk Disclosure, matching the
 *     standard-industry copy used by peers like trybobby.com.
 *
 * Rendered on every page except the legal pages themselves.
 */
export default function DisclaimerStrip() {
  const { pathname } = useLocation();
  const [expanded, setExpanded] = useState(false);
  if (pathname === "/terms" || pathname === "/privacy") return null;

  return (
    <>
      <div
        className="fixed bottom-0 inset-x-0 z-40 border-t backdrop-blur-md text-[10px] sm:text-[11px] leading-relaxed"
        style={{
          background: "rgba(3, 1, 16, 0.9)",
          borderColor: "rgba(168, 85, 247, 0.22)",
          color: "rgba(230, 230, 245, 0.6)",
        }}
        data-testid="disclaimer-strip"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-1.5 flex flex-wrap justify-center sm:justify-between items-center gap-1 sm:gap-3">
          <span className="tracking-wide flex items-center gap-2">
            <ShieldAlert className="w-3 h-3" style={{ color: "rgba(245, 166, 35, 0.85)" }} />
            <span>
              Educational platform — not investment advice.
              <span className="hidden sm:inline"> Trading is risky; you can lose your entire investment.</span>
            </span>
          </span>
          <div className="flex gap-3 items-center">
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="hover:text-mb-emerald underline-offset-2 hover:underline inline-flex items-center gap-1"
              data-testid="disclaimer-details-btn"
            >
              <Info className="w-3 h-3" />
              Full disclosure
            </button>
            <Link to="/terms" className="hover:text-mb-emerald underline-offset-2 hover:underline" data-testid="disclaimer-terms-link">Terms</Link>
            <Link to="/privacy" className="hover:text-mb-emerald underline-offset-2 hover:underline" data-testid="disclaimer-privacy-link">Privacy</Link>
          </div>
        </div>
      </div>

      {expanded && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
          style={{ background: "rgba(3, 1, 16, 0.75)", backdropFilter: "blur(6px)" }}
          onClick={() => setExpanded(false)}
          data-testid="disclaimer-modal"
        >
          <div
            className="relative w-full max-w-2xl rounded-xl border overflow-hidden"
            style={{ background: "#0f0c1d", borderColor: "rgba(168, 85, 247, 0.35)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "rgba(168, 85, 247, 0.2)" }}>
              <div className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5" style={{ color: "#F5A623" }} />
                <h2 className="font-display font-semibold text-lg text-white">Full disclosure</h2>
              </div>
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="p-1 rounded hover:bg-white/10"
                aria-label="Close"
                data-testid="disclaimer-modal-close"
              >
                <X className="w-4 h-4 text-white/70" />
              </button>
            </div>
            <div className="p-6 space-y-5 text-sm leading-relaxed text-neutral-300 max-h-[70vh] overflow-y-auto">
              <section>
                <h3 className="text-xs font-bold uppercase tracking-widest text-mb-emerald mb-2">Educational tool notice</h3>
                <p>
                  Moonbucks is designed as an <strong className="text-white">educational platform</strong> to help traders
                  understand institutional trading signals and market patterns. Moonbucks is not a registered investment
                  advisor and does not provide personalized investment advice.
                </p>
              </section>
              <section>
                <h3 className="text-xs font-bold uppercase tracking-widest text-mb-emerald mb-2">Risk disclosure</h3>
                <p>
                  Trading stocks, options, and other securities carries <strong className="text-white">substantial risk of loss</strong>
                  {" "}and is not suitable for every investor. Past performance does not guarantee future results. The
                  valuation of stocks and other securities may fluctuate, and as a result, you may lose more than your
                  original investment. All trading decisions are made at your own discretion and risk.
                  Please trade responsibly and only with capital you can afford to lose.
                </p>
              </section>
              <section className="pt-3 border-t text-xs text-neutral-500" style={{ borderColor: "rgba(168, 85, 247, 0.15)" }}>
                See the full <Link to="/terms" className="text-mb-emerald hover:underline">Terms</Link>{" "}
                and <Link to="/privacy" className="text-mb-emerald hover:underline">Privacy policy</Link>.
              </section>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
